import { useEffect, useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { addDays, clock, fmtDate, fmtDateShort, hours, hoursShort, isoDate, money, today } from "../format.js";
import {
  Async, Avatar, Badge, Button, Card, Check, Empty, ErrorPanel, Field, Fields, Grid, Input, KPI,
  Modal, Ribbon, Select, Table, Tabs, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

/** Monday of the current week, as YYYY-MM-DD. */
const mondayOfThisWeek = () => {
  const iso = today();
  const dow = new Date(`${iso}T00:00:00Z`).getUTCDay(); // 0 = Sunday
  return addDays(iso, -((dow + 6) % 7));
};

const sumMinutes = (rows) => rows.reduce((s, r) => s + Number(r.minutes || 0), 0);

export default function Time() {
  const { me, settings, projects, items } = useApp();
  const canLog = !!me.permissions["time.own"];
  const canInvoice = !!me.permissions["invoices.manage"];
  const state = useAsync(() => api.get("time"), []);
  const toast = useToast();
  const sym = settings.currency_symbol;
  const activeProjects = projects.filter((p) => p.status === "active");

  const [tab, setTab] = useState("entries");
  const [logging, setLogging] = useState(null); // an entry to edit, or {} for a new one

  /* ---- timer state lives here so a data reload can't unmount it mid-run ---- */
  const [draft, setDraft] = useState({ project_id: "", item_id: "", note: "" });
  const [running, setRunning] = useState(null); // { project_id, item_id, note, startedAt }
  const [secs, setSecs] = useState(0);
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setSecs(Math.round((Date.now() - running.startedAt) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [running]);

  function start() {
    const project_id = draft.project_id || activeProjects[0]?.id;
    if (!project_id) return toast.push("Create an active project before tracking time.", "err");
    setSecs(0);
    setRunning({ ...draft, project_id, startedAt: Date.now() });
  }

  async function stop() {
    setStopping(true);
    try {
      await api.post("time", {
        project_id: running.project_id,
        item_id: running.item_id || null,
        note: running.note || "Timed entry",
        minutes: Math.max(1, Math.round(secs / 60)),
      });
      setDraft({ project_id: running.project_id, item_id: running.item_id, note: "" });
      setRunning(null);
      setSecs(0);
      toast.push("Time logged");
      state.reload();
    } catch (e) {
      toast.push(e.message, "err");
    } finally {
      setStopping(false);
    }
  }

  const runningProject = running && projects.find((p) => p.id === running.project_id);
  const runningItem = running && items.find((i) => i.id === running.item_id);

  return (
    <>
      <TopBar title="Time tracking">
        {canLog && <Button onClick={() => setLogging({})}>＋ Log time manually</Button>}
      </TopBar>

      <Wrap>
        <Async state={state}>
          {(d) => {
            const seeAll = !!d.can_see_all;
            const ws = mondayOfThisWeek();
            const week = d.entries.filter((t) => isoDate(t.entry_date) >= ws);
            const mine = week.filter((t) => t.user_id === me.id);
            const unbilledValue = d.unbilled.reduce((s, u) => s + Number(u.value || 0), 0);
            const unbilledMinutes = sumMinutes(d.unbilled);

            return (
              <>
                <Grid cols={!canLog ? "1fr 1fr 1fr" : seeAll ? "1.3fr 1fr 1fr 1fr" : "1.3fr 1fr 1fr"}>
                  {canLog && (
                  <Card>
                    <div className="row" style={{ gap: 14 }}>
                      <div className="timer">{clock(secs)}</div>
                      <div className="grow" />
                      {running ? (
                        <Button busy={stopping} onClick={stop}>
                          ■ Stop &amp; save
                        </Button>
                      ) : (
                        <Button kind="pri" onClick={start}>
                          ▶ Start
                        </Button>
                      )}
                    </div>

                    {running ? (
                      <div className="row sm mt" style={{ gap: 7 }}>
                        <span className="dot" />
                        <b>{runningProject?.name || "Project"}</b>
                        <span className="muted">· {runningItem?.name || "No service"}</span>
                      </div>
                    ) : (
                      <>
                        <Fields cols={2} className="mt">
                          <Select
                            value={draft.project_id || activeProjects[0]?.id || ""}
                            onChange={(e) => setDraft((f) => ({ ...f, project_id: e.target.value }))}
                          >
                            {activeProjects.length ? null : <option value="">No active projects</option>}
                            {activeProjects.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </Select>
                          <Select
                            value={draft.item_id}
                            onChange={(e) => setDraft((f) => ({ ...f, item_id: e.target.value }))}
                          >
                            <option value="">No service</option>
                            {items.map((i) => (
                              <option key={i.id} value={i.id}>
                                {i.name}
                              </option>
                            ))}
                          </Select>
                        </Fields>
                        <div style={{ marginTop: 9 }}>
                          <Input
                            value={draft.note}
                            placeholder="What are you working on?"
                            onChange={(e) => setDraft((f) => ({ ...f, note: e.target.value }))}
                          />
                        </div>
                      </>
                    )}
                  </Card>
                  )}

                  <KPI label="My week" value={hours(sumMinutes(mine))} sub={`${mine.length} entries since ${fmtDateShort(ws)}`} />
                  {seeAll && (
                    <KPI label="Team week" value={hours(sumMinutes(week))} sub={`${week.length} entries this week`} />
                  )}
                  <KPI
                    label="Unbilled value"
                    value={money(unbilledValue, sym)}
                    sub={`${hours(unbilledMinutes)} hours`}
                  />
                </Grid>

                <div className="mt2">
                  <Tabs
                    value={tab}
                    onChange={setTab}
                    tabs={[
                      { key: "entries", label: "Entries" },
                      { key: "timesheet", label: "Weekly timesheet" },
                      { key: "unbilled", label: "Ready to invoice" },
                    ]}
                  />
                </div>

                {tab === "entries" && (
                  <EntriesTab entries={d.entries} seeAll={seeAll} sym={sym} onEdit={canLog ? setLogging : null} />
                )}
                {tab === "timesheet" && <TimesheetTab entries={d.entries} seeAll={seeAll} />}
                {tab === "unbilled" && (
                  <UnbilledTab unbilled={d.unbilled} entries={d.entries} sym={sym} canInvoice={canInvoice} />
                )}
              </>
            );
          }}
        </Async>
      </Wrap>

      {logging && (
        <LogTimeModal
          entry={logging}
          canPickPerson={!!state.data?.can_see_all}
          onClose={() => setLogging(null)}
          onSaved={state.reload}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- entries */

function EntriesTab({ entries, seeAll, sym, onEdit }) {
  const days = [...new Set(entries.map((t) => isoDate(t.entry_date)))].sort().reverse();
  if (!days.length)
    return (
      <Card bare>
        <Empty icon="◷">No time logged yet — start the timer or log an entry manually</Empty>
      </Card>
    );

  return (
    <>
      {days.map((day) => {
        const rows = entries.filter((t) => isoDate(t.entry_date) === day);
        return (
          <Card
            key={day}
            bare
            className="mt"
            title={day === today() ? "Today" : fmtDate(day)}
            right={<span className="sm dim num">{hoursShort(sumMinutes(rows))}</span>}
          >
            <Table
              rows={rows}
              columns={[
                seeAll && {
                  key: "who",
                  label: "Who",
                  render: (t) => (
                    <div className="stack">
                      <Avatar name={t.user_name} size={24} />
                      <span className="sm">{(t.user_name || "").split(" ")[0]}</span>
                    </div>
                  ),
                },
                {
                  key: "project",
                  label: "Project",
                  render: (t) => (
                    <>
                      <span className="sm">{t.project_name}</span>
                      <div className="tiny muted">{t.client_name}</div>
                    </>
                  ),
                },
                { key: "item", label: "Service", render: (t) => <span className="sm dim">{t.item_name || "—"}</span> },
                { key: "note", label: "Note", render: (t) => <span className="sm dim">{t.note || "—"}</span> },
                {
                  key: "minutes",
                  label: "Duration",
                  align: "right",
                  render: (t) => <span className="num">{hoursShort(t.minutes)}</span>,
                },
                {
                  key: "value",
                  label: "Value",
                  align: "right",
                  render: (t) =>
                    t.billable ? <span className="num">{money(t.value, sym)}</span> : <span className="muted">—</span>,
                },
                {
                  key: "status",
                  label: "Status",
                  render: (t) =>
                    t.invoice_id ? (
                      <Badge status="billed" />
                    ) : t.billable ? (
                      <Badge status="unbilled" />
                    ) : (
                      <Badge status="internal" />
                    ),
                },
                {
                  key: "edit",
                  label: "",
                  align: "right",
                  render: (t) =>
                    onEdit ? (
                      <Button
                        sm
                        kind="gho"
                        disabled={!!t.invoice_id}
                        title={t.invoice_id ? "Already invoiced — void the invoice to release it" : undefined}
                        onClick={() => onEdit(t)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <span className="muted">›</span>
                    ),
                },
              ]}
            />
          </Card>
        );
      })}
    </>
  );
}

/* -------------------------------------------------------------- timesheet */

function TimesheetTab({ entries, seeAll }) {
  const days = Array.from({ length: 8 }, (_, i) => addDays(today(), i - 7)); // oldest → today
  const inRange = entries.filter((t) => days.includes(isoDate(t.entry_date)));

  const rows = [...new Set(inRange.map((t) => t.project_id))].map((pid) => {
    const es = inRange.filter((t) => t.project_id === pid);
    const cells = days.map((day) => sumMinutes(es.filter((t) => isoDate(t.entry_date) === day)));
    return {
      id: pid,
      name: es[0].project_name,
      client_name: es[0].client_name,
      cells,
      total: cells.reduce((a, b) => a + b, 0),
    };
  });

  const colTotals = days.map((day) => sumMinutes(inRange.filter((t) => isoDate(t.entry_date) === day)));
  const grand = colTotals.reduce((a, b) => a + b, 0);

  return (
    <Card
      bare
      className="mt"
      title={`Week of ${fmtDateShort(days[0])}`}
      right={<span className="sm dim">{seeAll ? "everyone" : "just you"}</span>}
    >
      <Table
        rows={rows}
        empty={<Empty icon="◷">Nothing logged in the last 8 days</Empty>}
        columns={[
          {
            key: "project",
            label: "Project",
            render: (r) => (
              <>
                <b className="sm">{r.name}</b>
                <div className="tiny muted">{r.client_name}</div>
              </>
            ),
          },
          ...days.map((day, i) => ({
            key: day,
            label: fmtDateShort(day).split(" ")[1],
            align: "right",
            render: (r) =>
              r.cells[i] ? <span className="num sm">{hours(r.cells[i])}</span> : <span className="muted">—</span>,
          })),
          {
            key: "total",
            label: "Total",
            align: "right",
            render: (r) => <b className="num">{hours(r.total)}</b>,
          },
        ]}
        footer={
          rows.length ? (
            <tr style={{ background: "var(--panel-2)" }}>
              <td>
                <b>Total</b>
              </td>
              {colTotals.map((m, i) => (
                <td key={days[i]} className="right num">
                  <b>{m ? hours(m) : "—"}</b>
                </td>
              ))}
              <td className="right num">
                <b>{hours(grand)}</b>
              </td>
            </tr>
          ) : null
        }
      />
    </Card>
  );
}

/* -------------------------------------------------------------- ready to invoice */

function UnbilledTab({ unbilled, entries, sym, canInvoice }) {
  if (!unbilled.length)
    return (
      <Card bare>
        <Empty icon="◈">Nothing waiting to be invoiced — every billable hour is on an invoice</Empty>
      </Card>
    );

  return (
    <>
      <Ribbon>
        ◷ Unbilled time is grouped by client. Pulling it into an invoice marks the entries billed automatically.
      </Ribbon>

      {unbilled.map((u) => {
        const rows = entries.filter((t) => t.client_id === u.client_id && t.billable && !t.invoice_id);
        return (
          <Card
            key={u.client_id}
            bare
            className="mt"
            title={
              <div className="stack">
                <Avatar name={u.client_name} />
                <div>
                  <b>{u.client_name}</b>
                  <div className="tiny muted">{hours(u.minutes)} hours unbilled</div>
                </div>
              </div>
            }
            right={
              <>
                <b className="num">{money(u.value, sym)}</b>
                {canInvoice && (
                  <Button sm kind="pri" onClick={() => navigate("/invoices/new")}>
                    Invoice this
                  </Button>
                )}
              </>
            }
          >
            <Table
              rows={rows}
              empty={<Empty icon="◷">Entries are outside the current window</Empty>}
              columns={[
                {
                  key: "date",
                  label: "Date",
                  width: 100,
                  render: (t) => <span className="sm dim">{fmtDateShort(t.entry_date)}</span>,
                },
                { key: "project", label: "Project", render: (t) => <span className="sm">{t.project_name}</span> },
                { key: "note", label: "Note", render: (t) => <span className="sm dim">{t.note || "—"}</span> },
                {
                  key: "minutes",
                  label: "Hours",
                  align: "right",
                  width: 90,
                  render: (t) => <span className="num">{hours(t.minutes)} h</span>,
                },
                {
                  key: "value",
                  label: "Value",
                  align: "right",
                  width: 110,
                  render: (t) => <span className="num">{money(t.value, sym)}</span>,
                },
              ]}
            />
          </Card>
        );
      })}
    </>
  );
}

/* -------------------------------------------------------------- log time modal */

function LogTimeModal({ entry, canPickPerson, onClose, onSaved }) {
  const { me, projects, items, users } = useApp();
  const toast = useToast();
  const isNew = !entry.id;
  const activeProjects = projects.filter((p) => p.status === "active" || p.id === entry.project_id);

  const [form, setForm] = useState({
    project_id: entry.project_id || activeProjects[0]?.id || "",
    item_id: entry.item_id || "",
    entry_date: isoDate(entry.entry_date) || today(),
    hours: entry.minutes ? (Number(entry.minutes) / 60).toFixed(2) : "",
    user_id: entry.user_id || me.id,
    note: entry.note || "",
    billable: entry.billable !== false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const body = () => ({
    project_id: form.project_id,
    item_id: form.item_id || null,
    entry_date: form.entry_date,
    minutes: Math.round((Number(form.hours) || 0) * 60),
    note: form.note,
    billable: form.billable,
    user_id: canPickPerson ? form.user_id : undefined,
  });

  async function run(fn, message) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      toast.push(message);
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(() => (isNew ? api.post("time", body()) : api.put(`time/${entry.id}`, body())), isNew ? "Time logged" : "Entry updated");

  const remove = () => run(() => api.del(`time/${entry.id}`), "Entry deleted");

  return (
    <Modal
      title={isNew ? "Log time" : "Edit time entry"}
      width={480}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <Button kind="dgr" busy={busy} style={{ marginRight: "auto" }} onClick={remove}>
              Delete
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            {isNew ? "Log time" : "Save entry"}
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Fields cols={2}>
          <Field label="Project">
            <Select value={form.project_id} onChange={(e) => set("project_id", e.target.value)}>
              <option value="">Pick a project…</option>
              {activeProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Service">
            <Select value={form.item_id} onChange={(e) => set("item_id", e.target.value)}>
              <option value="">No service</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </Select>
          </Field>
        </Fields>

        <Fields cols={canPickPerson ? 3 : 2}>
          <Field label="Date">
            <Input type="date" value={form.entry_date} onChange={(e) => set("entry_date", e.target.value)} />
          </Field>
          <Field label="Hours">
            <Input value={form.hours} placeholder="2.5" onChange={(e) => set("hours", e.target.value)} />
          </Field>
          {canPickPerson && (
            <Field label="Person">
              <Select value={form.user_id} onChange={(e) => set("user_id", e.target.value)}>
                {users
                  .filter((u) => u.status !== "disabled")
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
              </Select>
            </Field>
          )}
        </Fields>

        <Field label="Note">
          <Input value={form.note} placeholder="What did you work on?" onChange={(e) => set("note", e.target.value)} />
        </Field>

        <Check label="Billable" checked={form.billable} onChange={(v) => set("billable", v)} />
      </Fields>
    </Modal>
  );
}
